/*  ReferralPipelineWidget — Admin Overview embed for the referrals hub.
 *
 *  iter100.203 — Replaces the old DemoLinkActivityWidget. Reads
 *  /api/admin/referrals/kpis and shows a compact 3-tile snapshot of
 *  the growth funnel. Self-hides when there's no activity in the
 *  window.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    ArrowRight, UserCheck, Trophy, Warning,
} from "@phosphor-icons/react";
import AdminSectionShell from "@/components/admin/AdminSectionShell";


const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;


export default function ReferralPipelineWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/referrals/kpis?days=7")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading || !data) return null;

    const activity = (data.engaged || 0) + (data.converted || 0);
    if (activity === 0 && (data.pending_referrals || 0) === 0 && (data.hot_unassigned || 0) === 0) {
        // Nothing this week — self-hide.
        return null;
    }

    /* iter100.245 — Drop the redundant "Open referrals hub" nav link
       from the header. Cards inside this section already link to
       referrals; the header should carry context, not another button.
       Show the total 7-day pipeline count instead. */
    const totalInPipeline = (data.engaged || 0) + (data.converted || 0);

    return (
        <AdminSectionShell
            eyebrow="Referral pipeline"
            tone="teal"
            description="Doctor demo-link referrals — last 7 days."
            right={
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground tabular-nums" data-testid="admin-referral-widget-pipeline-count">
                    {totalInPipeline} in pipeline
                </span>
            }
            bodyPad
            testid="admin-referral-pipeline-widget"
        >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Tile
                    Icon={UserCheck}
                    tone="amber"
                    label="Engaged"
                    value={data.engaged || 0}
                    sub={data.engaged > 0 ? "warm leads" : "none yet"}
                    to="/admin/referrals?stage=engaged"
                    testid="admin-referral-widget-engaged"
                />
                <Tile
                    Icon={Trophy}
                    tone="emerald"
                    label="Converted"
                    value={data.converted || 0}
                    sub={data.converted > 0 ? `${data.conversion_rate_pct}% conv. rate` : "none yet"}
                    to="/admin/referrals?stage=converted"
                    testid="admin-referral-widget-converted"
                />
                <Tile
                    Icon={Warning}
                    tone={data.hot_unassigned > 0 ? "rose" : "muted"}
                    label="Unassigned"
                    value={data.hot_unassigned || 0}
                    sub={data.hot_unassigned > 0 ? "assign to sales" : "all triaged"}
                    to="/admin/referrals?stage=engaged&assigned_to=unassigned"
                    testid="admin-referral-widget-unassigned"
                />
            </div>
            {(data.pending_referrals || 0) > 0 && (
                <div className="mt-3 flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2">
                    <div className="text-[12px] text-amber-900">
                        <strong>{data.pending_referrals}</strong> referral reward{data.pending_referrals === 1 ? "" : "s"} awaiting your approval
                        {(data.rewards_paid_inr || 0) > 0 && <span className="text-amber-800/80"> · {fmtINR(data.rewards_paid_inr)} paid this window</span>}
                    </div>
                    <Link
                        to="/admin/referrals?reward_status=pending"
                        className="text-[11px] font-semibold text-amber-900 hover:underline inline-flex items-center gap-1"
                        data-testid="admin-referral-widget-review-pending"
                    >
                        Review <ArrowRight size={11} weight="bold" />
                    </Link>
                </div>
            )}
        </AdminSectionShell>
    );
}


/* iter100.252 — Neutralised tile so all Overview sections read the
   same way as Platform Pulse: neutral base + tinted icon only.
   Colours reveal on hover via `hover:bg-<tone>-50/60`. */
function Tile({ Icon, tone, label, value, sub, to, testid }) {
    const hoverBg = {
        amber:   "hover:bg-amber-50/60",
        emerald: "hover:bg-emerald-50/60",
        rose:    "hover:bg-rose-50/60",
        muted:   "hover:bg-secondary/50",
    }[tone] || "hover:bg-secondary/50";
    const iconTone = {
        amber:   "text-amber-600",
        emerald: "text-emerald-600",
        rose:    "text-rose-600",
        muted:   "text-muted-foreground",
    }[tone] || "text-primary";
    return (
        <Link
            to={to}
            className={`group px-4 py-3 rounded-xl bg-card border border-border transition-colors ${hoverBg} hover:border-primary/30`}
            data-testid={testid}
        >
            <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                <Icon size={12} weight="duotone" className={iconTone} />
                <span className="truncate">{label}</span>
            </div>
            <div className="mt-1 font-display text-[22px] font-bold tabular-nums leading-none text-foreground">{value}</div>
            {sub && <div className="text-[10.5px] text-muted-foreground mt-1 truncate">{sub}</div>}
        </Link>
    );
}
